import { Component } from 'react'
import {
  AppState,
  View,
  Platform,
  type AppStateStatus,
  type ColorSchemeName,
  type NativeEventSubscription,
} from 'react-native'
import RNRestart from 'react-native-restart'
import { Appearance } from 'react-native'
import NetInfo, {
  type NetInfoChangeHandler,
  type NetInfoSubscription,
} from '@react-native-community/netinfo'
import PushNotificationIOS from '@react-native-community/push-notification-ios'
import PushNotification from 'react-native-push-notification'
import 'react-native-url-polyfill/auto'
import { connect, type MapDispatchToProps, type MapStateToProps } from 'react-redux'
import * as SystemUI from 'expo-system-ui'
import debounce from 'lodash/debounce'
import * as RNLocalize from 'react-native-localize'
import VoipPushNotification, {
  type EventsPayload as VoipEventsPayload,
} from 'react-native-voip-push-notification'
import type { DefaultTheme } from 'styled-components/native'

import { getThemeName } from 'APP/Theme/AppThemeProvider'
import * as defaultThemes from 'APP/Theme/defaultThemes'

import { getColourScheme } from 'APP/Helpers/AppearanceUtils'
import { isAndroid, isMobile, isWeb } from 'APP/Helpers/checkPlatform'
import { getPrimaryLoginStrategy } from 'APP/Lib/ConfigHelpers'

// Redux
import StartupActions from 'APP/Redux/StartupRedux'
import AppSessionActions from 'APP/Redux/AppSessionRedux'
import LoginActions from 'APP/Redux/LoginRedux'
import DeeplinkActions from 'APP/Redux/DeeplinkRedux'
import HabitsActions from 'APP/Redux/HabitsRedux'
import type { RootState } from 'APP/Store/CreateStore'
import type { AppSessionState } from 'APP/Store/AppSession/types'
import type { DeeplinkState } from 'APP/Store/Deeplink/types'
import type { FeaturesState } from 'APP/Store/Features/types'

// Components
import InAppMessageNotification from 'APP/Components/InAppMessageNotification'
import DebugTools from 'APP/Components/DebugTools'
import Toast from 'APP/Components/Toast'
import { ToastContainer as GlobalToastContainer } from 'APP/Converse/Toast/ToastContainer'
import { navigationRef as Nav, NavigationRoot, PREVENT_DARK_MODE_RESTART_ROUTES } from 'APP/Nav'

// Services
import Config from 'APP/Config'
import {
  configureNotifications,
  getChannelIdFromNotification,
  getDataFromNotification,
  getDeeplinkFromNotification,
  NotificationCategory,
  type ConfigureNotificationsParams,
  type NotificationData,
  type TypedNotification,
} from 'APP/Lib/NotificationHelpers'
import Analytics from 'APP/Services/Analytics'
import { openUrl } from 'APP/Services/Branch'
// Styles
import styles from './style'
import { AlertRoot } from 'APP/Converse/Alert'
import IdleTimer from 'APP/Lib/IdleTimer'
import { nativeCallAndroidActions } from 'APP/Store/NativeCallAndroid'
import { nativeCallIOSActions } from 'APP/Store/NativeCallIOS'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { logger } from 'APP/Lib/Datadog'
import type {
  IncomingFullScreenCallPayload,
  NotificationCallAcceptedPayload,
} from 'APP/Store/NativeCallAndroid/types'
import { NativeCallDisplayedPayload } from 'APP/Store/NativeCallIOS/types'
import { selectIsAppActive } from 'APP/Sagas/AppSessionSagas'
import AccountLinkingGuard from 'APP/Components/AccountLinkingGuard'

const themes = defaultThemes as Record<string, DefaultTheme>

const getCurrentTheme = () => {
  const colorScheme = getColourScheme()
  const themeName = getThemeName(colorScheme, Config.BRAND_ID, Config.BRAND_VERSION)
  return themes[themeName] || themes.diaLight
}

export const getCoreFeatureFlags = (features: FeaturesState) => {
  // WAIT WHY ARE WE OVERRIDING THE TYPE?
  // With the current type, we can't delete keys from this object because they're required,
  // but we don't want them to be included in the analytics context for cleanliness.
  // And since we re-type the object when returning, this is a scoped override that allows us to keep our analytics clean.
  const baseFeatures = { ...features } as Record<string, unknown>
  delete baseFeatures.ldFlags
  delete baseFeatures.overrides

  return baseFeatures as FeaturesState
}

interface DispatchProps {
  background: () => void
  foreground: () => void
  silentLoginWithPrimaryConnection: () => void
  syncAppState: (appState: AppStateStatus) => void
  incomingFullScreenCall: (payload: IncomingFullScreenCallPayload) => void
  launchStartupSequence: () => void
  nativeCallDisplayed: (payload: NativeCallDisplayedPayload) => void
  notificationCallAccepted: (payload: NotificationCallAcceptedPayload) => void
  registerToken: (pushToken: string) => void
  registerTokenFailure: (pushToken: null, error: string) => void
  registerVoipToken: (pushVoipToken: string) => void
  setDeferredDeeplink: (path: DeeplinkState['path'], props: DeeplinkState['props']) => void
  // TODO: ensure we only pass a Boolean to this method, for correctness
  setIsConnected: (isConnected: boolean | null) => void
  updateLocalize: () => void
}

interface StateProps {
  features: FeaturesState
  isActive: boolean
  isLoggingIn: boolean
  accessToken: string | null | undefined
  shouldIgnoreAppStateChanges: boolean
  showDebugFab: boolean
  systemErrors: AppSessionState['systemErrors']
}

interface OwnProps {
  isNavigationReady: boolean
}

type Props = DispatchProps & StateProps & OwnProps

type NonSensitiveData = Partial<{
  deeplink: string
  brandId: string
  category: string
  channelId: string
  sessionId: string
}>

class RootContainer extends Component<Props> {
  appStateSubscription: NativeEventSubscription | undefined = undefined
  appearanceChangeSubscription: NativeEventSubscription | undefined = undefined
  colorScheme: ColorSchemeName
  colorSchemeRestartTimeout: NodeJS.Timeout | undefined = undefined
  unsubscribeConnectionListener: NetInfoSubscription | undefined = undefined
  voipListenersAdded = false
  _handleVisibilityChange: (() => void) | undefined = undefined

  constructor(props: Props) {
    super(props)

    configureNotifications({
      onRegister: this._handlePushNotificationRegistration,
      onNotification: this._handleRemoteNotification,
      onInitialNotification: this._handleInitialNotification,
      onRegistrationError: this._handlePushNotificationRegistrationError,
    })

    this._attachVoipPushListeners()

    this.colorScheme = getColourScheme()
  }

  componentDidMount() {
    logger.info('RootContainer component did mount', {
      currentAppState: AppState.currentState,
      isActive: this.props.isActive,
    })
    this.unsubscribeConnectionListener = () => {}

    NetInfo.fetch().then((state) => {
      this.props.setIsConnected(state.isConnected)
    })
    this.unsubscribeConnectionListener = NetInfo.addEventListener(this._handleNetworkChange)

    this.appearanceChangeSubscription = Appearance.addChangeListener(this._handledAppearanceChange)
    this.appStateSubscription = AppState.addEventListener('change', this._handleEventChange)

    const appState = AppState?.currentState
    // If app is foregrounded and isActive is false, launch startup sequence
    // This handles the case where the app resumes but app state change event was missed
    // e.g. on Android after pausing and resuming the app
    if (appState === 'active' && !this.props.isActive) {
      this.props.launchStartupSequence()
    }

    // When embedded on Android, if a user navigates to the host app and then back to embedded,
    // the RootContainer is mounted here while isActive is `false`. Nothing calls `goToForeground`,
    // so isActive in the Redux store remains outdated and incorrect when the embedded app
    // is actually in an "active" state. Need to sync the app state to the store on remounts to
    // avoid this issue. Longer term, consider launching goToForeground here when embedded
    if (isEmbeddedEnvironment()) {
      this.props.syncAppState(AppState.currentState)
    }

    if (isMobile()) {
      RNLocalize.addEventListener('change', () => {
        this.props.updateLocalize()
      })
    }

    if (isWeb() && getPrimaryLoginStrategy()) {
      this._handleVisibilityChange = () => {
        if (
          document.visibilityState === 'visible' &&
          this.props.isActive &&
          !this.props.isLoggingIn
        ) {
          if (this.props.accessToken) {
            this.props.silentLoginWithPrimaryConnection()
          } else {
            this.props.foreground()
          }
        }
      }
      document.addEventListener('visibilitychange', this._handleVisibilityChange)
    }

    if (Platform.OS !== 'ios') {
      // Do we actually need this on ios? It causes a bug with iOS modals
      // (white background with white content looks weird)
      const currentTheme = getCurrentTheme()
      SystemUI.setBackgroundColorAsync(currentTheme.colors.background)
    }

    const baseFeatures = getCoreFeatureFlags(this.props.features)
    Analytics.attachEntity('launch_darkly', baseFeatures)
  }

  componentWillUnmount() {
    clearTimeout(this.colorSchemeRestartTimeout)
    this.unsubscribeConnectionListener?.()
    this.appStateSubscription?.remove()
    this.appearanceChangeSubscription?.remove()
    this._detachVoipPushListeners()
    if (this._handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this._handleVisibilityChange)
    }
  }

  /* istanbul ignore next */
  componentDidUpdate(prevProps: Props) {
    // Update entity values just in case features changed
    /* istanbul ignore next */
    if (prevProps.features !== this.props.features) {
      /* istanbul ignore next */
      const baseFeatures = getCoreFeatureFlags(this.props.features)
      /* istanbul ignore next */
      Analytics.attachEntity('launch_darkly', baseFeatures)
    }
  }

  /* istanbul ignore next */
  /*
    Current implementation of dark mode support requires a bundle reload.
    Some screens with unrecoverable states do not support this, so we retry
    until the user is on a supported screen.
  */
  colorSchemeRestartCheck = () => {
    if (this.colorScheme !== getColourScheme()) {
      const routeName = Nav?.getCurrentRoute()?.name

      if (routeName) {
        const shouldPreventRestartForRoute = PREVENT_DARK_MODE_RESTART_ROUTES.includes(routeName)
        if (shouldPreventRestartForRoute) {
          this.colorSchemeRestartTimeout = setTimeout(this.colorSchemeRestartCheck, 5000)
          return
        }
      }

      RNRestart.Restart()
    }
  }

  /*
    Debouncing to handle a bug in Appearance which causes it to trigger an event with
    incorrect colorScheme when the app is backgrounded, followed momentarily by the correct value.
    https://github.com/expo/expo/issues/10815
  */
  _handledAppearanceChange = debounce(this.colorSchemeRestartCheck, 1000)

  _handleNotificationCategory = (notifData: NotificationData) => {
    if (isAndroid()) {
      if (notifData?.category === NotificationCategory.ACCEPT_CALL) {
        const { sessionId } = notifData as { sessionId: string }
        logger.info('Accepting call from Android non-fullscreen notification', { sessionId })
        this.props.notificationCallAccepted({ sessionId })
      } else if (notifData?.category === NotificationCategory.FULL_SCREEN_CALL) {
        // NB - the payload received here doesn't tell us if the user accepted or declined the call
        const { sessionId } = notifData as { sessionId: string }
        logger.info('Responded to Android fullscreen call', { sessionId })
        this.props.incomingFullScreenCall({ sessionId })
      }
    }
  }

  _buildNonSensitiveData = (
    notification: TypedNotification,
    additionalData: NonSensitiveData = {}
  ): NonSensitiveData => {
    const notifData = getDataFromNotification(notification)
    if (!notifData) return additionalData

    const { brandId, category, channelId, sessionId } = notifData
    return { ...additionalData, brandId, category, channelId, sessionId }
  }

  _handleInitialNotification: ConfigureNotificationsParams['onInitialNotification'] = (
    notification
  ) => {
    if (!notification) return
    const channelId = getChannelIdFromNotification(notification)
    const notifData = getDataFromNotification(notification)

    if (channelId) {
      this.props.setDeferredDeeplink('conversation', { channelId })
    }

    if (notifData) {
      this._handleNotificationCategory(notifData)
    }

    const nonSensitiveData = this._buildNonSensitiveData(notification)
    Analytics.trackEvent('push_notification_processed', {
      ...notification,
      ...nonSensitiveData,
    })
  }

  _handleRemoteNotification: ConfigureNotificationsParams['onNotification'] = (notification) => {
    // See if there's a deeplink in the notification, if there is it's an ✨ E N G A G E M E N T ✨ notification
    const deeplink = getDeeplinkFromNotification(notification)
    const notifData = getDataFromNotification(notification)

    // Select the specific payload fields to include when logging the notification data
    // This is necessary to avoid logging PHI/PII
    const { data: _data, ...scrubbedNotification } = notification
    const nonSensitiveData = this._buildNonSensitiveData(notification, { deeplink })
    logger.info('Responded to remote notification', {
      notification: scrubbedNotification,
      payload: nonSensitiveData,
    })

    Analytics.trackEvent('push_notification_processed', {
      ...scrubbedNotification,
      ...nonSensitiveData,
    })

    if (deeplink) {
      // Forward link to Branch, which will cause DeeplinkSaga.watchDeeplinkRequests to be triggered
      // Not reachable on web, so we don't need to worry about this until we implement notifications on web
      openUrl(deeplink)
    } else {
      if (notifData) {
        this._handleNotificationCategory(notifData)
      }

      // Received notification in the background, technically only fires on iOS afaik but just double check before calling these APIs
      if (Platform.OS === 'ios') {
        // Only tick the badge over for messaging notifications
        if (!this.props.isActive && getChannelIdFromNotification(notification)) {
          PushNotification.setApplicationIconBadgeNumber(1)
        }
      }
      notification.finish(PushNotificationIOS.FetchResult.NoData) //required
    }
  }

  _handleVoIPNotification = (notification: VoipEventsPayload['notification']) => {
    // CallKit UI is already triggered via RNCallKeep.reportNewIncomingCall (see AppDelegate.swift), so we only log here.
    // Select the specific payload fields to include when logging the notification data
    // This is necessary to avoid logging PHI/PII
    // NB - the fields available here are all the fields from the raw notification (see Engage models)
    const { brandId, hasVideo, sessionId, uuid } = notification as {
      brandId: string
      hasVideo: boolean
      sessionId: string | undefined
      uuid: string | undefined
    }

    if (typeof sessionId !== 'string' || !sessionId || typeof uuid !== 'string' || !uuid) {
      logger.error('Received VoIP notification with invalid payload', {
        payload: { brandId, hasVideo, sessionId, uuid },
      })
      return
    }

    logger.info('Received VoIP notification', { payload: { brandId, hasVideo, sessionId, uuid } })
    this.props.nativeCallDisplayed({ sessionId, callUUID: uuid })
  }

  _handlePushNotificationRegistration: ConfigureNotificationsParams['onRegister'] = (
    tokenResult
  ) => {
    const { token } = tokenResult

    // This is to ensure the registration functionality triggered by the native OS does
    // not override the explicitly set token, set by the host device
    if (isEmbeddedEnvironment()) return

    if (token && token.length > 0) {
      this.props.registerToken(token)
    }
  }

  _handlePushNotificationRegistrationError: ConfigureNotificationsParams['onRegistrationError'] = (
    error
  ) => {
    this.props.registerTokenFailure(null, `Failed to get device token. Error: ${error}`)
  }

  _handleEventChange = (appState: AppStateStatus) => {
    logger.debug('Handling AppState change', {
      shouldIgnoreAppStateChanges: this.props.shouldIgnoreAppStateChanges,
      isActive: this.props.isActive,
      currentAppState: appState,
    })
    // When shouldIgnoreAppStateChanges is active (e.g., during IntakePhoto file selection),
    // ignore both foreground and background events to prevent:
    // - Location updates and app lock from interfering (foreground)
    // - Mattermost from stopping (background)
    if (this.props.shouldIgnoreAppStateChanges) return

    if (appState === 'active' && !this.props.isActive) {
      this.props.foreground()
    }
    if (appState === 'background') {
      this.props.background()
    }
  }

  _handleNetworkChange: NetInfoChangeHandler = (state) => {
    this.props.setIsConnected(state.isInternetReachable)
  }

  _attachVoipPushListeners = () => {
    // Note: Android does not have a dedicated voip push notifications flow,
    // And instead uses regular FCM push notifications for VoIP calls
    if (Platform.OS === 'ios' && !this.voipListenersAdded) {
      // 1. Listen for incoming VoIP notifications
      VoipPushNotification.addEventListener('notification', this._handleVoIPNotification)

      // 2. Handle cached events (if the app was killed and launched by a VoIP push)
      // This event ensures that if the app was launched by a VoIP push when it was terminated,
      // you can still process the notification once the JS bridge is ready.
      VoipPushNotification.addEventListener('didLoadWithEvents', (events) => {
        if (!events || !Array.isArray(events) || events.length < 1) return

        for (const notification of events) {
          const { name, data } = notification
          if (name === VoipPushNotification.RNVoipPushRemoteNotificationsRegisteredEvent) {
            this.props.registerVoipToken(data)
          } else if (name === VoipPushNotification.RNVoipPushRemoteNotificationReceivedEvent) {
            this._handleVoIPNotification(data)
          }
        }
      })

      // This is to ensure the registration functionality triggered by the native OS
      // does not override the explicitly set token, set by the host device
      // CHECKME: Could maybe be moved to line 253
      if (isEmbeddedEnvironment()) return

      // 3. Setup listening for the VoIP push token, send token to APN provider server
      VoipPushNotification.addEventListener('register', (token) => {
        this.props.registerVoipToken(token)
      })

      this.voipListenersAdded = true

      // 4. Register for VoIP Push Token
      VoipPushNotification.registerVoipToken()
    }
  }

  _detachVoipPushListeners = () => {
    if (Platform.OS === 'ios' && this.voipListenersAdded) {
      VoipPushNotification.removeEventListener('notification')
      VoipPushNotification.removeEventListener('register')
      VoipPushNotification.removeEventListener('didLoadWithEvents')
      this.voipListenersAdded = false
    }
  }

  render() {
    const { isNavigationReady } = this.props

    // Keep NavigationRoot under a stable wrapper so readiness changes preserve navigator state.
    return (
      <View style={styles.applicationView}>
        <AccountLinkingGuard>
          <NavigationRoot />
        </AccountLinkingGuard>
        <InAppMessageNotification />
        {this.props.showDebugFab && <DebugTools />}
        <Toast />
        <AlertRoot />
        <IdleTimer />
        {isNavigationReady && <GlobalToastContainer />}
      </View>
    )
  }
}

const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => {
  const showDebugFab = (__DEV__ || Config.ENABLE_DEBUG_MENU) && state.app.showDebugFab
  return {
    showDebugFab,
    isActive: selectIsAppActive(state),
    isLoggingIn: state.login.isLoggingIn,
    accessToken: state.login.accessToken,
    systemErrors: state.appSession.systemErrors,
    features: state.features,
    shouldIgnoreAppStateChanges: state.history.shouldIgnoreAppStateChanges,
  }
}

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (dispatch) => ({
  foreground: () => dispatch(AppSessionActions.setAppForegrounded()),
  silentLoginWithPrimaryConnection: () => {
    const connection = getPrimaryLoginStrategy()?.connection
    if (connection) {
      dispatch(
        LoginActions.silentLoginRequestWithConnection(connection, { triggerLogoutOnFailure: false })
      )
    }
  },
  background: () => dispatch(AppSessionActions.setAppBackgrounded()),
  syncAppState: (appState: AppStateStatus) => dispatch(AppSessionActions.syncAppState(appState)),
  launchStartupSequence: () => dispatch(StartupActions.launchStartupSequence()),
  setIsConnected: (isConnected) => dispatch(AppSessionActions.setIsConnected(isConnected)),
  registerToken: (pushToken) => dispatch(StartupActions.setPushTokenRequest(pushToken)),
  registerVoipToken: (pushVoipToken) =>
    dispatch(StartupActions.setPushVoipTokenRequest(pushVoipToken)),
  registerTokenFailure: (pushToken, error) =>
    dispatch(StartupActions.setPushTokenFailure(pushToken, error, 0)),
  setDeferredDeeplink: (path, props) => dispatch(DeeplinkActions.setDeferredDeeplink(path, props)),
  updateLocalize: () => dispatch(HabitsActions.ensureNotificationParity()),
  nativeCallDisplayed: (payload) => dispatch(nativeCallIOSActions.nativeCallDisplayed(payload)),
  notificationCallAccepted: (payload) =>
    dispatch(nativeCallAndroidActions.notificationCallAccepted(payload)),
  incomingFullScreenCall: (payload) =>
    dispatch(nativeCallAndroidActions.incomingFullScreenCall(payload)),
})

export default connect(mapStateToProps, mapDispatchToProps)(RootContainer)
